import React, { useContext } from "react";

import InfoToolTip from "@/components/muiCustom/tooltip/InfoToolTip";
import { GlobalContext } from "@/helpers/contexts/contexts";

type TooltipContentKey =
  | "estimated_distribution"
  | "distribution_cost"
  | "store_copy"
  | "store_copy_cost"
  | "mailing_date"
  | "market";

type TooltipContent =
  | string
  | ((_value: string | number | null | undefined) => string);

const toolTipContent = {
  estimated_distribution:
    "Expected number of mailers to be delivered to consumer households in addition to the national distribution.",
  distribution_cost:
    "Cost to distribute the mailers (includes print, shipping, and taxes).",
  store_copy: (brandName) =>
    `Store copy is delivered to your ${brandName} location in increments of 1,000. Select Yes or No if you would like to receive additional store copies to hand out to customers in-store. These copies are in addition to any store copies included in the national campaign.`,
  store_copy_cost: (storeCopyCpm) =>
    `${formatTooltipCurrency(storeCopyCpm)} per increments of 1,000.`,
  mailing_date:
    "Pre-determined mailing week based on the national campaign mailing window.",
  market: "The DMA your store falls within, based on data from Corporate.",
} satisfies Record<TooltipContentKey, TooltipContent>;

type NationalPlusUpToolTipsProps = {
  type?: TooltipContentKey | "";
  customContent?: string | null;
  cpm?: number | string;
};

function NationalPlusUpToolTips({
  type = "",
  customContent = null,
  cpm = 0,
}: NationalPlusUpToolTipsProps) {
  const { selectedBrand } = useContext(GlobalContext);
  const brandName =
    (selectedBrand as { brand_name?: string } | undefined)?.brand_name ||
    "brand";

  let content = customContent || "";
  if (!content && type) {
    const staticOrDynamicContent = toolTipContent[type];
    content =
      typeof staticOrDynamicContent === "function"
        ? getDynamicContent(type, brandName, cpm)
        : staticOrDynamicContent;
  }

  return <InfoToolTip content={content} />;
}

export default NationalPlusUpToolTips;

function getDynamicContent(
  type: TooltipContentKey,
  brandName: string,
  cpm: number | string
) {
  if (type === "store_copy_cost") {
    return (toolTipContent[type] as (_value: number | string) => string)(cpm);
  }

  return (toolTipContent[type] as (_value: string) => string)(brandName);
}

function formatTooltipCurrency(value: number | string | null | undefined) {
  const numericValue = Number(value) || 0;
  return `$${numericValue.toLocaleString("en-US", {
    minimumFractionDigits: numericValue % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}
